import { useState } from "react";
import { useTranslation } from "react-i18next";
import PublicLayout from "@/components/PublicLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Search, Wallet, Clock } from "lucide-react";

export default function GiftCardCheck() {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setData(null); setLoading(true);
    try {
      const res = await api.get(`/giftcards/check/${encodeURIComponent(code.trim().toUpperCase())}`);
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || t("gift.check_invalid"));
    } finally { setLoading(false); }
  };

  return (
    <PublicLayout>
      <div className="max-w-2xl mx-auto px-6 py-16">
        <div className="mb-8 text-center">
          <div className="overline text-accent">{t("gift.check_overline")}</div>
          <h1 className="mt-2 text-3xl sm:text-4xl font-heading font-bold tracking-tight">{t("gift.check_title")}</h1>
        </div>

        <Card className="rounded-3xl border-2">
          <CardContent className="p-6 sm:p-8">
            <form onSubmit={submit} className="flex gap-3" data-testid="giftcard-check-form">
              <Input
                placeholder={t("gift.check_placeholder")}
                aria-label={t("gift.check_placeholder")}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="rounded-full h-12 text-center font-mono tracking-widest text-lg"
                data-testid="giftcard-check-input"
                required
              />
              <Button type="submit" disabled={loading} size="lg" className="rounded-full h-12" data-testid="giftcard-check-btn">
                <Search className="w-4 h-4 mr-2" /> {t("gift.check_btn")}
              </Button>
            </form>
            {error && <div className="mt-4 text-sm text-destructive text-center" data-testid="giftcard-check-error">{error}</div>}
            {data && (
              <div className="mt-8 space-y-4" data-testid="giftcard-check-result">
                <div className="text-center p-6 rounded-2xl bg-primary text-primary-foreground">
                  <div className="opacity-80 text-xs uppercase tracking-widest">{t("gift.balance_available")}</div>
                  <div className="text-5xl font-heading font-bold mt-2">{data.current_balance.toFixed(2)} €</div>
                  <div className="mt-2 text-xs opacity-70">{t("gift.initial_of", { amount: data.initial_amount.toFixed(2) })} · <span className="uppercase">{data.status}</span></div>
                </div>
                <div>
                  <div className="text-sm font-heading font-semibold flex items-center gap-2 mb-3">
                    <Clock className="w-4 h-4" aria-hidden="true" /> {t("gift.movements")}
                  </div>
                  {(data.transactions || []).length === 0 ? (
                    <div className="text-sm text-muted-foreground">{t("gift.no_movements")}</div>
                  ) : (
                    <div className="divide-y divide-border">
                      {data.transactions.map((tx, i) => (
                        <div key={i} className="py-2 flex items-center justify-between text-sm">
                          <div>
                            <div className="font-medium">{tx.description || (tx.type === "redeem" ? t("gift.redeem") : t("gift.topup"))}</div>
                            <div className="text-xs text-muted-foreground">{new Date(tx.created_at).toLocaleDateString()}</div>
                          </div>
                          <div className={`font-mono font-semibold ${tx.amount < 0 ? "text-destructive" : "text-emerald-600"}`}>
                            {tx.amount > 0 ? "+" : ""}{tx.amount.toFixed(2)} €
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </PublicLayout>
  );
}
